"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Img } from "./Img";
import {
  AppRoutesEnum,
  CookiesEnum,
  cookiesValues,
  SPECIAL_ACCESS_BANNER_ANIMATE_IN_TIMEOUT,
  URLQueryParamsEnum,
  EventNamesEnum,
  EventPropertiesEnum,
  SpecialAccessWidgetTypeEnum,
} from "@/constants/constants";
import { Text } from "./Text";
import Link from "next/link";
import {
  ConsumptionStateCategoryEnum,
  SpecialAccessDataWithContent,
} from "@/screens/specialAccess/types/specialAccess.types";
import { getClientSideCookieValue } from "@/utils/common.helpers";
import { Box } from "./Box";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import { PathNames } from "./LayoutCSR";
import { GreenStatusIcon, RedStatusIcon } from "./Icons";

const SpecialAccessBanner = ({
  currentSpecialAccessData,
  totalConsumptionStates,
  pathname,
}: {
  totalConsumptionStates: number;
  currentSpecialAccessData: SpecialAccessDataWithContent;
  pathname?: PathNames;
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const { trackEvent } = useTrackingAnalytics();
  const hasTrackedViewEvent = useRef(false);

  // Get contentSlug from currentSpecialAccessData
  const contentSlug = currentSpecialAccessData?.content?.slug;

  // Get watch page URL from cookies (set by server component)
  const watchPageUrl = getClientSideCookieValue(
    cookiesValues[CookiesEnum.SpecialAccessWatchPageUrl],
  );

  // Determine link href based on state value
  const linkHref = useMemo(() => {
    if (!watchPageUrl) return AppRoutesEnum.CHECKOUT;

    const currentStateValue = currentSpecialAccessData.current_state_value + 1;

    if (
      currentSpecialAccessData.current_state_category ===
        ConsumptionStateCategoryEnum.Consumption &&
      currentStateValue < totalConsumptionStates
    ) {
      // Link to watch page
      return decodeURIComponent(watchPageUrl);
    } else {
      // Link to checkout with redirectionAfterPaymentTo
      return `${AppRoutesEnum.CHECKOUT}?${URLQueryParamsEnum.REDIRECTION_AFTER_PAYMENT_TO}=${watchPageUrl}`;
    }
  }, [
    watchPageUrl,
    currentSpecialAccessData.current_state_value,
    totalConsumptionStates,
  ]);

  // Animate in after mount
  useEffect(() => {
    const timer = setTimeout(
      () => setIsVisible(true),
      SPECIAL_ACCESS_BANNER_ANIMATE_IN_TIMEOUT,
    );
    return () => clearTimeout(timer);
  }, []);

  // Track SPECIAL_ACCESS_WIDGET_VIEW event when banner becomes visible
  useEffect(() => {
    if (isVisible && contentSlug && !hasTrackedViewEvent.current) {
      trackEvent({
        eventName: EventNamesEnum.SPECIAL_ACCESS_WIDGET_VIEW,
        properties: {
          [EventPropertiesEnum.CONTENT_SLUG]: contentSlug,
          [EventPropertiesEnum.WIDGET_TYPE]:
            SpecialAccessWidgetTypeEnum.FLOATING_BANNER,
          [EventPropertiesEnum.WIDGET_SOURCE]: pathname,
          [EventPropertiesEnum.CTA_NAME]:
            currentSpecialAccessData.floating_banner?.cta?.text,
        },
      });
      hasTrackedViewEvent.current = true;
    }
  }, [
    isVisible,
    contentSlug,
    trackEvent,
    pathname,
    currentSpecialAccessData.floating_banner?.cta?.text,
  ]);

  return (
    <div
      data-testid="special-access-floating-banner"
      className={`align-center mx-4 mb-4 flex transform items-center justify-between gap-0 rounded-lg bg-stg-floating-banner-background p-3 backdrop-blur-sm transition-all duration-500 ease-in-out ${
        isVisible
          ? "translate-y-0"
          : "pointer-events-none -translate-y-6 opacity-0"
      }`}
    >
      <Box position="relative">
        <Box
          height="h-10"
          width="w-10"
          overflow="hidden"
          borderRadius={2}
          position="relative"
        >
          <Img
            src={`${currentSpecialAccessData?.content?.thumbnail?.vertical}`}
            alt={currentSpecialAccessData?.content?.title || ""}
            fill={true}
            objectFit="cover"
          />
        </Box>
        {/* Status icon - Green for Consumption, Red for Terminal */}
        <div className="absolute right-0.5 top-0.5 -translate-y-1/2 translate-x-1/2">
          {currentSpecialAccessData.current_state_category ===
          ConsumptionStateCategoryEnum.Consumption ? (
            <GreenStatusIcon />
          ) : (
            <RedStatusIcon />
          )}
        </div>
      </Box>
      <div className="ms-1 flex min-w-0 flex-1 items-center justify-between whitespace-nowrap">
        <div className="me-auto ms-1.5 flex flex-col gap-0.5 text-wrap">
          <Text size={2} weight="bold" appearance="black">
            {currentSpecialAccessData.floating_banner?.title || ""}
          </Text>
          <div className="text-stg-grey-2">
            <Text size={1} weight="medium" dangerouslyDisableDefaultTextColor>
              {currentSpecialAccessData.floating_banner?.description || ""}
            </Text>
          </div>
        </div>
        <Link
          data-testid="special-access-floating-banner-link"
          href={linkHref}
          className={`rounded-lg p-2`}
          style={{
            backgroundColor: `#${currentSpecialAccessData.floating_banner?.cta?.bg_color}`,
            color: `#${currentSpecialAccessData.floating_banner?.cta?.text_color}`,
          }}
          onClick={() => {
            // Track SPECIAL_ACCESS_WIDGET_CLICKED event
            if (contentSlug) {
              trackEvent({
                eventName: EventNamesEnum.SPECIAL_ACCESS_WIDGET_CLICKED,
                properties: {
                  [EventPropertiesEnum.CONTENT_SLUG]: contentSlug,
                  [EventPropertiesEnum.WIDGET_TYPE]:
                    SpecialAccessWidgetTypeEnum.FLOATING_BANNER,
                  [EventPropertiesEnum.WIDGET_SOURCE]: pathname,
                  [EventPropertiesEnum.CTA_NAME]:
                    currentSpecialAccessData.floating_banner?.cta?.text,
                },
              });
            }
          }}
        >
          <Text size={1} weight="medium" dangerouslyDisableDefaultTextColor>
            {currentSpecialAccessData.floating_banner?.cta?.text}
          </Text>
        </Link>
      </div>
    </div>
  );
};

export default SpecialAccessBanner;
